import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Upload, Download, X, Loader2, Image as ImageIcon, Wand2, MousePointer, Eraser, RotateCcw, Eye, EyeOff } from 'lucide-react';
import { saveAs } from 'file-saver';

const BackgroundRemover = () => {
  const [originalImage, setOriginalImage] = useState(null);
  const [processedImage, setProcessedImage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  
  // New states for lasso tool and selection
  const [tool, setTool] = useState('auto'); // 'auto', 'lasso', 'brush', 'eraser'
  const [isDrawing, setIsDrawing] = useState(false);
  const [selectionPath, setSelectionPath] = useState([]);
  const [subjectMask, setSubjectMask] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [brushSize, setBrushSize] = useState(20);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);
  const imageCanvasRef = useRef(null);
  const selectionCanvasRef = useRef(null);

  // Handle file upload with progress
  const handleFileUpload = useCallback(async (file) => {
    if (!file || !file.type.startsWith('image/')) {
      alert('Please select a valid image file');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setProcessedImage(null);
    setSelectionPath([]);
    setSubjectMask(null);
    setShowPreview(false);

    try {
      const reader = new FileReader();
      
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + Math.random() * 15;
        });
      }, 100);

      reader.onload = (e) => {
        clearInterval(progressInterval);
        setUploadProgress(100);
        setOriginalImage(e.target.result);
        setIsUploading(false);
        
        // Get image dimensions
        const img = new Image();
        img.onload = () => {
          setImageDimensions({ width: img.width, height: img.height });
        };
        img.src = e.target.result;
        
        // Reset progress after a short delay
        setTimeout(() => setUploadProgress(0), 1000);
      };
      
      reader.onerror = () => {
        clearInterval(progressInterval);
        setIsUploading(false);
        setUploadProgress(0);
        alert('Error reading file. Please try again.');
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error processing file:', error);
      alert('Error processing file. Please try again.');
      setIsUploading(false);
      setUploadProgress(0);
    }
  }, []);

  // Handle drag and drop
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  }, [handleFileUpload]);

  // Lasso tool functionality
  const getMousePos = useCallback((e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  }, []);

  const handleCanvasMouseDown = useCallback((e) => {
    if (!originalImage || tool === 'auto') return;
    
    const canvas = selectionCanvasRef.current;
    if (!canvas) return;
    
    const pos = getMousePos(e, canvas);
    setIsDrawing(true);
    
    if (tool === 'lasso') {
      setSelectionPath([pos]);
    } else if (tool === 'brush' || tool === 'eraser') {
      drawBrushStroke(pos, tool === 'eraser');
    }
  }, [originalImage, tool, getMousePos]);

  const handleCanvasMouseMove = useCallback((e) => {
    if (!isDrawing || !originalImage || tool === 'auto') return;
    
    const canvas = selectionCanvasRef.current;
    if (!canvas) return;
    
    const pos = getMousePos(e, canvas);
    
    if (tool === 'lasso') {
      setSelectionPath(prev => [...prev, pos]);
      drawLassoPath([...selectionPath, pos]);
    } else if (tool === 'brush' || tool === 'eraser') {
      drawBrushStroke(pos, tool === 'eraser');
    }
  }, [isDrawing, originalImage, tool, getMousePos, selectionPath]);

  const handleCanvasMouseUp = useCallback((e) => {
    if (!isDrawing || !originalImage) return;
    
    setIsDrawing(false);
    
    if (tool === 'lasso' && selectionPath.length > 2) {
      // Close the lasso path
      const closedPath = [...selectionPath, selectionPath[0]];
      setSelectionPath(closedPath);
      drawLassoPath(closedPath);
      createMaskFromPath(closedPath);
    }
  }, [isDrawing, originalImage, tool, selectionPath]);

  const drawLassoPath = useCallback((path) => {
    const canvas = selectionCanvasRef.current;
    if (!canvas || path.length < 2) return;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]);
    ctx.beginPath();
    ctx.moveTo(path[0].x, path[0].y);
    
    for (let i = 1; i < path.length; i++) {
      ctx.lineTo(path[i].x, path[i].y);
    }
    
    ctx.stroke();
  }, []);

  const drawBrushStroke = useCallback((pos, isEraser = false) => {
    const canvas = selectionCanvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.globalCompositeOperation = isEraser ? 'destination-out' : 'source-over';
    ctx.fillStyle = isEraser ? 'rgba(0,0,0,1)' : 'rgba(59, 130, 246, 0.3)';
    
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, brushSize / 2, 0, 2 * Math.PI);
    ctx.fill();
    
    // Update subject mask
    updateSubjectMask();
  }, [brushSize]);

  const createMaskFromPath = useCallback((path) => {
    if (path.length < 3) return;
    
    const canvas = document.createElement('canvas');
    canvas.width = imageDimensions.width;
    canvas.height = imageDimensions.height;
    const ctx = canvas.getContext('2d');
    
    // Scale path coordinates to match original image dimensions
    const displayCanvas = selectionCanvasRef.current;
    if (!displayCanvas) return;
    
    const scaleX = imageDimensions.width / displayCanvas.width;
    const scaleY = imageDimensions.height / displayCanvas.height;
    
    // Create mask using scaled path
    ctx.fillStyle = 'white';
    ctx.beginPath();
    ctx.moveTo(path[0].x * scaleX, path[0].y * scaleY);
    
    for (let i = 1; i < path.length; i++) {
      ctx.lineTo(path[i].x * scaleX, path[i].y * scaleY);
    }
    
    ctx.closePath();
    ctx.fill();
    
    // Get mask data
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const mask = new Array(canvas.width * canvas.height).fill(0);
    
    for (let i = 0; i < imageData.data.length; i += 4) {
      const pixelIndex = i / 4;
      mask[pixelIndex] = imageData.data[i] > 128 ? 1 : 0;
    }
    
    setSubjectMask(mask);
    setShowPreview(true);
  }, [imageDimensions]);

  const updateSubjectMask = useCallback(() => {
    // This would update the subject mask based on brush strokes
    // Implementation depends on how you want to handle the mask
  }, []);

  // Enhanced background removal with lasso support
  const removeBackground = useCallback(async () => {
    if (!originalImage) return;

    setIsProcessing(true);
    setProcessingProgress(10);
    
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = originalImage;
      });

      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      // Set canvas size to match image
      const { width, height } = img;
      canvas.width = width;
      canvas.height = height;

      // Draw original image
      ctx.drawImage(img, 0, 0);
      setProcessingProgress(20);

      // Get image data
      const imageData = ctx.getImageData(0, 0, width, height);
      const data = imageData.data;
      const processedData = new Uint8ClampedArray(data);

      let finalMask;

      if (subjectMask && tool !== 'auto') {
        // Use manual selection (lasso/brush)
        setProcessingProgress(50);
        finalMask = subjectMask;
      } else {
        // AI-powered automatic detection
        setProcessingProgress(30);
        
        // Enhanced AI algorithm for better subject detection
        finalMask = await performAIBackgroundRemoval(data, width, height);
        setProcessingProgress(70);
      }

      // Apply mask with improved feathering
      for (let i = 0; i < data.length; i += 4) {
        const pixelIndex = i / 4;
        const alpha = Math.round(finalMask[pixelIndex] * 255);
        processedData[i + 3] = alpha;
      }

      setProcessingProgress(90);
      
      // Apply the processed data
      const newImageData = new ImageData(processedData, width, height);
      ctx.putImageData(newImageData, 0, 0);
      
      // Convert to blob and set as processed image
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        setProcessedImage(url);
        setIsProcessing(false);
        setProcessingProgress(0);
      }, 'image/png');
      
    } catch (error) {
      console.error('Error processing image:', error);
      alert('Error processing image. Please try again.');
      setIsProcessing(false);
    }
  }, [originalImage, subjectMask, tool]);

  // Enhanced AI background removal algorithm - SIMPLIFIED AND WORKING
  const performAIBackgroundRemoval = useCallback(async (data, width, height) => {
    const mask = new Array(width * height).fill(1); // Start with all pixels as subject
    
    // Step 1: Simple but effective edge detection
    const edgeMap = new Array(width * height).fill(0);
    const sobelX = [-1, 0, 1, -2, 0, 2, -1, 0, 1];
    const sobelY = [-1, -2, -1, 0, 0, 0, 1, 2, 1];
    
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const pixelIndex = y * width + x;
        let gx = 0, gy = 0;
        
        for (let ky = -1; ky <= 1; ky++) {
          for (let kx = -1; kx <= 1; kx++) {
            const neighborIndex = (y + ky) * width + (x + kx);
            const neighborIdx = neighborIndex * 4;
            const gray = (data[neighborIdx] + data[neighborIdx + 1] + data[neighborIdx + 2]) / 3;
            const kernelIndex = (ky + 1) * 3 + (kx + 1);
            gx += gray * sobelX[kernelIndex];
            gy += gray * sobelY[kernelIndex];
          }
        }
        
        const magnitude = Math.sqrt(gx * gx + gy * gy);
        edgeMap[pixelIndex] = magnitude;
      }
    }
    
    // Step 2: Find background colors from corners and edges
    const backgroundSamples = [];
    const sampleSize = 5;
    
    // Sample all four corners
    const cornerSize = Math.min(width, height) * 0.15;
    for (let x = 0; x < cornerSize; x += sampleSize) {
      for (let y = 0; y < cornerSize; y += sampleSize) {
        const idx = (y * width + x) * 4;
        backgroundSamples.push([data[idx], data[idx + 1], data[idx + 2]]);
      }
    }
    
    // Sample edges
    for (let x = 0; x < width; x += sampleSize) {
      const topIdx = x * 4;
      const bottomIdx = ((height - 1) * width + x) * 4;
      backgroundSamples.push([data[topIdx], data[topIdx + 1], data[topIdx + 2]]);
      backgroundSamples.push([data[bottomIdx], data[bottomIdx + 1], data[bottomIdx + 2]]);
    }
    
    for (let y = 0; y < height; y += sampleSize) {
      const leftIdx = (y * width) * 4;
      const rightIdx = (y * width + width - 1) * 4;
      backgroundSamples.push([data[leftIdx], data[leftIdx + 1], data[leftIdx + 2]]);
      backgroundSamples.push([data[rightIdx], data[rightIdx + 1], data[rightIdx + 2]]);
    }
    
    // Step 3: Find dominant background color
    const backgroundColors = [];
    const tolerance = 40;
    
    backgroundSamples.forEach(sample => {
      let found = false;
      for (let i = 0; i < backgroundColors.length; i++) {
        const dist = Math.sqrt(
          Math.pow(sample[0] - backgroundColors[i][0], 2) +
          Math.pow(sample[1] - backgroundColors[i][1], 2) +
          Math.pow(sample[2] - backgroundColors[i][2], 2)
        );
        if (dist < tolerance) {
          backgroundColors[i][3]++;
          found = true;
          break;
        }
      }
      if (!found) {
        backgroundColors.push([sample[0], sample[1], sample[2], 1]);
      }
    });
    
    backgroundColors.sort((a, b) => b[3] - a[3]);
    const primaryBgColor = backgroundColors[0] || [128, 128, 128];
    
    // Step 4: Create mask based on color similarity and edge strength
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const pixelIndex = y * width + x;
        const pixelIdx = pixelIndex * 4;
        
        const r = data[pixelIdx];
        const g = data[pixelIdx + 1];
        const b = data[pixelIdx + 2];
        
        // Calculate color distance from background
        const colorDist = Math.sqrt(
          Math.pow(r - primaryBgColor[0], 2) +
          Math.pow(g - primaryBgColor[1], 2) +
          Math.pow(b - primaryBgColor[2], 2)
        );
        
        // Get edge strength
        const edgeStrength = edgeMap[pixelIndex];
        
        // Determine if pixel is background
        let isBackground = false;
        
        // If it's near edges and has strong edge, likely subject
        if (edgeStrength > 30) {
          isBackground = false;
        }
        // If color is very similar to background and no strong edge
        else if (colorDist < 50 && edgeStrength < 20) {
          isBackground = true;
        }
        // If it's in corners or edges and similar color
        else if ((x < cornerSize || x > width - cornerSize || 
                 y < cornerSize || y > height - cornerSize) && 
                 colorDist < 60) {
          isBackground = true;
        }
        
        mask[pixelIndex] = isBackground ? 0 : 1;
      }
    }
    
    // Step 5: Apply morphological operations to clean up
    const cleanedMask = new Array(width * height).fill(0);
    const kernelSize = 3;
    
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const pixelIndex = y * width + x;
        let subjectCount = 0;
        let totalCount = 0;
        
        for (let dy = -Math.floor(kernelSize/2); dy <= Math.floor(kernelSize/2); dy++) {
          for (let dx = -Math.floor(kernelSize/2); dx <= Math.floor(kernelSize/2); dx++) {
            const nx = x + dx;
            const ny = y + dy;
            
            if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
              const neighborIndex = ny * width + nx;
              subjectCount += mask[neighborIndex];
              totalCount++;
            }
          }
        }
        
        cleanedMask[pixelIndex] = subjectCount > totalCount / 2 ? 1 : 0;
      }
    }
    
    // Step 6: Apply feathering for smooth edges
    const finalMask = new Array(width * height).fill(0);
    const featherRadius = 3;
    
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const pixelIndex = y * width + x;
        let weightedSum = 0;
        let totalWeight = 0;
        
        for (let dy = -featherRadius; dy <= featherRadius; dy++) {
          for (let dx = -featherRadius; dx <= featherRadius; dx++) {
            const nx = x + dx;
            const ny = y + dy;
            
            if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
              const neighborIndex = ny * width + nx;
              const distance = Math.sqrt(dx * dx + dy * dy);
              
              if (distance <= featherRadius) {
                const weight = Math.exp(-(distance * distance) / (2 * featherRadius * featherRadius));
                weightedSum += cleanedMask[neighborIndex] * weight;
                totalWeight += weight;
              }
            }
          }
        }
        
        finalMask[pixelIndex] = totalWeight > 0 ? weightedSum / totalWeight : cleanedMask[pixelIndex];
      }
    }
    
    return finalMask;
  }, []);

  // Debug function to test background removal
  const testBackgroundRemoval = useCallback(() => {
    console.log('Testing background removal...');
    console.log('Original image:', originalImage);
    console.log('Image dimensions:', imageDimensions);
    console.log('Current tool:', tool);
    console.log('Subject mask exists:', !!subjectMask);
  }, [originalImage, imageDimensions, tool, subjectMask]);

  // Download processed image
  const downloadImage = useCallback(() => {
    if (processedImage) {
      saveAs(processedImage, 'background-removed.png');
    }
  }, [processedImage]);

  // Reset everything
  const resetAll = useCallback(() => {
    setOriginalImage(null);
    setProcessedImage(null);
    setIsProcessing(false);
    setProcessingProgress(0);
    setIsUploading(false);
    setUploadProgress(0);
    setSelectionPath([]);
    setSubjectMask(null);
    setShowPreview(false);
    setTool('auto');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectionPath([]);
    setSubjectMask(null);
    setShowPreview(false);
    if (selectionCanvasRef.current) {
      const ctx = selectionCanvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, selectionCanvasRef.current.width, selectionCanvasRef.current.height);
    }
  }, []);

  // Initialize canvas when image loads
  useEffect(() => {
    if (originalImage && selectionCanvasRef.current) {
      const canvas = selectionCanvasRef.current;
      const img = new Image();
      img.onload = () => {
        // Set canvas size to match the displayed image size
        const container = canvas.parentElement;
        const containerRect = container.getBoundingClientRect();
        const aspectRatio = img.width / img.height;
        
        let displayWidth = containerRect.width;
        let displayHeight = containerRect.width / aspectRatio;
        
        if (displayHeight > containerRect.height) {
          displayHeight = containerRect.height;
          displayWidth = containerRect.height * aspectRatio;
        }
        
        canvas.width = displayWidth;
        canvas.height = displayHeight;
        canvas.style.width = displayWidth + 'px';
        canvas.style.height = displayHeight + 'px';
      };
      img.src = originalImage;
    }
  }, [originalImage]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Wand2 className="w-8 h-8 text-indigo-600 mr-3" />
            <h1 className="text-4xl font-bold text-gray-900">HD Background Remover</h1>
          </div>
          <p className="text-lg text-gray-600">
            Upload an image and remove its background with high quality results
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Upload Section */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
              <Upload className="w-6 h-6 mr-2 text-indigo-600" />
              Upload Image
            </h2>
            
            {!originalImage ? (
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActive 
                    ? 'border-indigo-500 bg-indigo-50' 
                    : 'border-gray-300 hover:border-indigo-400'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                {isUploading ? (
                  <div className="space-y-4">
                    <Loader2 className="w-16 h-16 text-indigo-600 mx-auto mb-4 animate-spin" />
                    <p className="text-lg text-gray-600 mb-2">
                      Uploading Image...
                    </p>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div 
                        className="bg-indigo-600 h-3 rounded-full transition-all duration-300 ease-out"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                    <p className="text-sm text-gray-500">
                      {Math.round(uploadProgress)}% Complete
                    </p>
                  </div>
                ) : (
                  <>
                    <ImageIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-lg text-gray-600 mb-2">
                      Drag & drop your image here
                    </p>
                    <p className="text-sm text-gray-500 mb-4">
                      or click to browse files
                    </p>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
                    >
                      Choose File
                    </button>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e.target.files[0])}
                  className="hidden"
                />
              </div>
            ) : (
              <div className="space-y-4">
                {/* Tool Selection */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <button
                    onClick={() => setTool('auto')}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      tool === 'auto' 
                        ? 'bg-indigo-600 text-white' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    <Wand2 className="w-4 h-4 inline mr-1" />
                    Auto AI
                  </button>
                  <button
                    onClick={() => setTool('lasso')}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      tool === 'lasso' 
                        ? 'bg-indigo-600 text-white' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    <MousePointer className="w-4 h-4 inline mr-1" />
                    Lasso
                  </button>
                  <button
                    onClick={() => setTool('brush')}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      tool === 'brush' 
                        ? 'bg-indigo-600 text-white' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    <Eraser className="w-4 h-4 inline mr-1" />
                    Brush
                  </button>
                  <button
                    onClick={() => setTool('eraser')}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      tool === 'eraser' 
                        ? 'bg-indigo-600 text-white' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    <Eraser className="w-4 h-4 inline mr-1" />
                    Eraser
                  </button>
                </div>

                {/* Brush Size Control */}
                {tool === 'brush' || tool === 'eraser' ? (
                  <div className="flex items-center gap-2 mb-4">
                    <label className="text-sm font-medium text-gray-700">Brush Size:</label>
                    <input
                      type="range"
                      min="5"
                      max="50"
                      value={brushSize}
                      onChange={(e) => setBrushSize(parseInt(e.target.value))}
                      className="flex-1"
                    />
                    <span className="text-sm text-gray-600 w-8">{brushSize}</span>
                  </div>
                ) : null}

                {/* Image with Selection Canvas */}
                <div className="relative">
                  <img
                    src={originalImage}
                    alt="Original"
                    className="w-full h-64 object-contain rounded-lg border"
                    style={{ 
                      backgroundImage: showPreview && subjectMask 
                        ? 'url("data:image/svg+xml,%3Csvg width="20" height="20" xmlns="http://www.w3.org/2000/svg"%3E%3Cdefs%3E%3Cpattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse"%3E%3Cpath d="M 20 0 L 0 0 0 20" fill="none" stroke="%23e5e7eb" stroke-width="1"/%3E%3C/pattern%3E%3C/defs%3E%3Crect width="100%25" height="100%25" fill="url(%23grid)"/%3E%3C/svg%3E")'
                        : 'none'
                    }}
                  />
                  <canvas
                    ref={selectionCanvasRef}
                    className="absolute top-0 left-0 cursor-crosshair"
                    style={{ 
                      pointerEvents: tool !== 'auto' ? 'auto' : 'none',
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                    onMouseDown={handleCanvasMouseDown}
                    onMouseMove={handleCanvasMouseMove}
                    onMouseUp={handleCanvasMouseUp}
                    onMouseLeave={() => setIsDrawing(false)}
                  />
                  <button
                    onClick={resetAll}
                    className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Selection Controls */}
                {tool !== 'auto' && (selectionPath.length > 0 || subjectMask) && (
                  <div className="flex gap-2">
                    <button
                      onClick={clearSelection}
                      className="px-3 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
                    >
                      <RotateCcw className="w-4 h-4 inline mr-1" />
                      Clear Selection
                    </button>
                    <button
                      onClick={() => setShowPreview(!showPreview)}
                      className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                    >
                      {showPreview ? <EyeOff className="w-4 h-4 inline mr-1" /> : <Eye className="w-4 h-4 inline mr-1" />}
                      {showPreview ? 'Hide' : 'Show'} Preview
                    </button>
                  </div>
                )}

                <div className="space-y-2">
                  <button
                    onClick={removeBackground}
                    disabled={isProcessing}
                    className="w-full bg-indigo-600 text-white py-3 px-4 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Processing... {processingProgress}%
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-5 h-5 mr-2" />
                        Remove Background
                      </>
                    )}
                  </button>
                  
                  {/* Debug button - remove in production */}
                  <button
                    onClick={testBackgroundRemoval}
                    className="w-full bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600 transition-colors text-sm"
                  >
                    Debug Info (Check Console)
                  </button>
                </div>
                
                {isProcessing && (
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div 
                      className="bg-indigo-600 h-2 rounded-full transition-all duration-300 ease-out"
                      style={{ width: `${processingProgress}%` }}
                    ></div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Result Section */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
              <Download className="w-6 h-6 mr-2 text-green-600" />
              Result
            </h2>
            
            {processedImage ? (
              <div className="space-y-4">
                <div className="relative">
                  <img
                    src={processedImage}
                    alt="Processed"
                    className="w-full h-64 object-contain rounded-lg border bg-gray-100"
                    style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width="20" height="20" xmlns="http://www.w3.org/2000/svg"%3E%3Cdefs%3E%3Cpattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse"%3E%3Cpath d="M 20 0 L 0 0 0 20" fill="none" stroke="%23e5e7eb" stroke-width="1"/%3E%3C/pattern%3E%3C/defs%3E%3Crect width="100%25" height="100%25" fill="url(%23grid)"/%3E%3C/svg%3E")' }}
                  />
                </div>
                <button
                  onClick={downloadImage}
                  className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Download PNG
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 border-2 border-dashed border-gray-300 rounded-lg">
                <div className="text-center text-gray-500">
                  <ImageIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>Processed image will appear here</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Hidden canvas for processing */}
        <canvas
          ref={canvasRef}
          className="hidden"
        />

        {/* Features */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg p-6 shadow-md text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Wand2 className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">AI-Powered</h3>
            <p className="text-gray-600">Advanced AI algorithms for automatic background detection</p>
          </div>
          
          <div className="bg-white rounded-lg p-6 shadow-md text-center">
            <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <MousePointer className="w-6 h-6 text-indigo-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Lasso Tool</h3>
            <p className="text-gray-600">Manual selection with lasso and brush tools for precision</p>
          </div>
          
          <div className="bg-white rounded-lg p-6 shadow-md text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Download className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">HD Quality</h3>
            <p className="text-gray-600">Download your processed image as PNG with transparency</p>
          </div>
          
          <div className="bg-white rounded-lg p-6 shadow-md text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ImageIcon className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">All Formats</h3>
            <p className="text-gray-600">Supports JPG, PNG, and other common image formats</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BackgroundRemover;
